import { CommonModule } from '@angular/common';
import { Component, computed, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { map } from 'rxjs/operators';

type MarketingSectionKey =
  | 'seo-geo'
  | 'google-ads'
  | 'facebook-instagram-ads'
  | 'email-marketing'
  | 'social-media-marketing'
  | 'referral-program'
  | 'google-business-profile';

interface MarketingSection {
  title: string;
  description: string;
  highlights: string[];
}

const DEFAULT_SECTION: MarketingSectionKey = 'seo-geo';

const SECTION_CONFIG: Record<MarketingSectionKey, MarketingSection> = {
  'seo-geo': {
    title: 'SEO & GEO',
    description: 'Search optimization workspace for website visibility, rankings, and local discovery.',
    highlights: ['Keyword clusters and on-page SEO tasks', 'Technical SEO health and indexing checks', 'Google Search Console and local profile sync']
  },
  'google-ads': {
    title: 'Google Ads',
    description: 'Campaign planning and performance tracking for paid search and display campaigns.',
    highlights: ['Campaign and ad group structure', 'Budget pacing and spend controls', 'Conversion tracking and cost-per-lead analysis']
  },
  'facebook-instagram-ads': {
    title: 'Facebook & Instagram Ads',
    description: 'Meta Ads campaign management for awareness, engagement, and lead generation.',
    highlights: ['Audience and creative testing', 'Campaign objective and budget controls', 'Performance reporting by audience and creative']
  },
  'email-marketing': {
    title: 'Email Marketing',
    description: 'Email list, segmentation, and campaign workflow for member and event communications.',
    highlights: ['Template and campaign calendar', 'Audience segmentation and automation flows', 'Open, click, and conversion performance']
  },
  'social-media-marketing': {
    title: 'Social Media Marketing',
    description: 'Organic social planning and publishing with content and channel performance insights.',
    highlights: ['Content calendar and channel planning', 'Post scheduling and approval workflow', 'Reach, engagement, and traffic attribution']
  },
  'referral-program': {
    title: 'Referral Program',
    description: 'Referral setup and tracking to scale member and event growth via trusted networks.',
    highlights: ['Referral links and code generation', 'Referral source attribution', 'Incentive tracking and payout reconciliation']
  },
  'google-business-profile': {
    title: 'Google Business Profile',
    description: 'Business profile operations for local search presence, reviews, and location updates.',
    highlights: ['Profile completeness and update queue', 'Review response workflow', 'Local visibility and action metrics']
  }
};

@Component({
  standalone: true,
  imports: [CommonModule],
  template: `
    <section class="marketing-shell">
      <header class="marketing-header">
        <div>
          <h1>{{ section().title }}</h1>
          <p>{{ section().description }}</p>
        </div>
        <span class="coming-soon">Coming soon</span>
      </header>

      <section class="coming-card">
        <h2>Planned capabilities</h2>
        <ul>
          <li *ngFor="let line of section().highlights">{{ line }}</li>
        </ul>
      </section>
    </section>
  `,
  styles: `
    :host {
      display: block;
      width: 100%;
      max-width: 1320px;
      box-sizing: border-box;
      margin: 0 auto;
    }

    .marketing-shell {
      display: grid;
      gap: 14px;
      width: 100%;
      max-width: 100%;
      box-sizing: border-box;
    }

    .marketing-header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      gap: 12px;
    }

    .marketing-header h1 {
      margin: 0;
      font-size: var(--type-page-title);
      line-height: 1.15;
      letter-spacing: -0.02em;
      font-weight: 700;
      color: var(--app-text);
    }

    .marketing-header p {
      margin: 0.3rem 0 0;
      font-size: var(--type-body);
      color: var(--app-muted);
      max-width: 820px;
    }

    .coming-soon {
      border: 1px solid #b7dff0;
      background: #eef6ff;
      color: var(--jci-blue);
      font-size: 0.76rem;
      font-weight: 700;
      border-radius: 999px;
      padding: 0.18rem 0.56rem;
      line-height: 1.3;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      white-space: nowrap;
      margin-top: 2px;
    }

    .coming-card {
      border: 1px solid var(--app-border);
      border-radius: 14px;
      background: var(--app-surface);
      padding: 1rem 1rem 1.05rem;
    }

    .coming-card h2 {
      margin: 0;
      font-size: var(--type-section-title);
      line-height: 1.2;
      letter-spacing: -0.01em;
      font-weight: 700;
      color: var(--app-text);
    }

    .coming-card ul {
      margin: 0.75rem 0 0;
      padding-left: 1rem;
      display: grid;
      gap: 0.48rem;
    }

    .coming-card li {
      color: var(--app-muted);
      font-size: var(--type-body);
      line-height: 1.45;
    }
  `
})
export class MarketingComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly sectionParam = toSignal(
    this.route.paramMap.pipe(map((params) => params.get('section') || DEFAULT_SECTION)),
    { initialValue: DEFAULT_SECTION }
  );

  readonly section = computed<MarketingSection>(() => {
    const key = this.sectionParam() as MarketingSectionKey;
    return SECTION_CONFIG[key] ?? SECTION_CONFIG[DEFAULT_SECTION];
  });
}
